import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ShieldX, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useProfile } from '@/features/profile/hooks/use-profile'
import { hasRole } from '@/types/profile'
import type { UserRole } from '@/types/profile'

interface RoleGuardProps {
  children: ReactNode
  requiredRole?: UserRole
}

export function RoleGuard({ children, requiredRole = 'system_admin' }: RoleGuardProps) {
  const { profile, loading } = useProfile()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!profile || !hasRole(profile.role, requiredRole)) {
    return (
      <div className="mx-auto max-w-md py-16">
        <Card>
          <CardContent className="flex flex-col items-center py-8 text-center">
            <ShieldX className="h-12 w-12 text-red-400" />
            <h2 className="mt-4 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="mt-2 text-sm text-gray-500">
              You do not have permission to view this page. This area is restricted to
              administrators.
            </p>
            <Link to="/" className="mt-6">
              <Button variant="primary">Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
